import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  COMMON_TIMEZONES,
  jobTitleSchema,
  permissionsForRole,
  type JobTitle,
  type NotificationPreferences,
} from '@lizrd/contracts';
import { exportMyData, sendWeeklyDigestPreview } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { useUiStore } from '../store/ui';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import { Toggle } from '../components/ui/toggle';
import { MfaCard } from '../components/security/MfaCard';

const JOB_TITLES = jobTitleSchema.options;

const PREF_LABELS: Record<keyof NotificationPreferences, string> = {
  memberActivity: 'Member activity (joins, role changes)',
  subscriptionAlerts: 'Subscription limit alerts',
  domainActivity: 'Domain changes',
  recommendations: 'Recommendations assigned to me',
  weeklyDigest: 'Weekly optimization digest (Mondays)',
};

const labelClass = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-muted';

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const logoutAll = useAuthStore((s) => s.logoutAll);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const [name, setName] = useState(user?.name ?? '');
  const [jobTitle, setJobTitle] = useState(user?.jobTitle ?? '');
  const [timezone, setTimezone] = useState(user?.timezone ?? '');
  const [prefs, setPrefs] = useState<NotificationPreferences>(
    user?.notificationPreferences ?? {
      memberActivity: true,
      subscriptionAlerts: true,
      domainActivity: true,
      recommendations: true,
      weeklyDigest: true,
    },
  );
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ busy: boolean; msg: string | null }>({
    busy: false,
    msg: null,
  });

  async function onSendPreview() {
    setPreview({ busy: true, msg: null });
    try {
      await sendWeeklyDigestPreview();
      setPreview({ busy: false, msg: 'Sent — check your inbox.' });
    } catch (err) {
      setPreview({
        busy: false,
        msg: err instanceof Error ? err.message : 'Could not send the preview.',
      });
    }
  }
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  async function onExportMyData() {
    setError(null);
    setExporting(true);
    try {
      const data = await exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lizrd-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not export your data');
    } finally {
      setExporting(false);
    }
  }

  if (!user) return null;

  async function onSignOutAll() {
    setSigningOut(true);
    try {
      await logoutAll();
      navigate('/login', { replace: true });
    } catch {
      setSigningOut(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    setError(null);
    setBusy(true);
    try {
      await updateProfile({
        name: name.trim(),
        jobTitle: jobTitle ? (jobTitle as JobTitle) : null,
        timezone: timezone || null,
        notificationPreferences: prefs,
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Avatar name={user.name} email={user.email} size={48} />
        <div>
          <h1 className="font-display text-2xl font-bold">Your profile</h1>
          <p className="text-sm text-muted">{user.email}</p>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {saved && <Alert variant="success">Saved.</Alert>}

      <Card className="p-6">
        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className={labelClass}>Name</span>
            <Input
              required
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setSaved(false);
              }}
            />
          </label>
          <label className="block">
            <span className={labelClass}>Job title</span>
            <Select
              value={jobTitle}
              onChange={(v) => {
                setJobTitle(v);
                setSaved(false);
              }}
              options={[
                { value: '', label: 'Not set' },
                ...JOB_TITLES.map((t) => ({ value: t, label: t })),
              ]}
              ariaLabel="Job title"
            />
          </label>
          <label className="block">
            <span className={labelClass}>Timezone</span>
            <Select
              value={timezone}
              onChange={(v) => {
                setTimezone(v);
                setSaved(false);
              }}
              options={[
                { value: '', label: 'Not set' },
                ...COMMON_TIMEZONES.map((tz) => ({ value: tz, label: tz })),
              ]}
              ariaLabel="Timezone"
            />
          </label>

          <div>
            <span className={labelClass}>Email notifications</span>
            <div className="space-y-2">
              {(Object.keys(PREF_LABELS) as (keyof NotificationPreferences)[]).map((key) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={prefs[key]}
                    onChange={(e) => {
                      setPrefs((p) => ({ ...p, [key]: e.target.checked }));
                      setSaved(false);
                    }}
                  />
                  {PREF_LABELS[key]}
                </label>
              ))}
            </div>
            {prefs.weeklyDigest && (
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={onSendPreview}
                  disabled={preview.busy}
                  className="text-xs font-semibold text-iris hover:text-iris-strong disabled:opacity-50"
                >
                  {preview.busy ? 'Sending…' : 'Send me a preview of the weekly digest'}
                </button>
                {preview.msg && <span className="text-xs text-muted">{preview.msg}</span>}
              </div>
            )}
          </div>

          <Button type="submit" disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </form>
      </Card>

      <Card className="p-6">
        <span className={labelClass}>Role &amp; permissions</span>
        <div className="mb-3">
          <Badge variant="neutral">{user.role.toLowerCase()}</Badge>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {permissionsForRole(user.role).map((p) => (
            <span key={p} className="rounded-sm bg-surface-2 px-2 py-0.5 text-xs text-haze">
              {p}
            </span>
          ))}
        </div>
      </Card>

      <MfaCard />

      <Card className="space-y-4 p-6">
        <span className={labelClass}>Appearance</span>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-bone">Dark mode</div>
            <p className="mt-0.5 text-xs leading-relaxed text-muted">
              Switch between the light and dark theme. Your choice is saved to this browser.
            </p>
          </div>
          <div className="flex-none pt-0.5">
            <Toggle
              checked={theme === 'dark'}
              onChange={(v) => setTheme(v ? 'dark' : 'light')}
              label="Dark mode"
            />
          </div>
        </div>
      </Card>

      <Card className="space-y-3 p-6">
        <span className={labelClass}>Your data</span>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-md text-xs leading-relaxed text-muted">
            Download a copy of the personal data Lizrd holds about you — your profile, the comments
            and activity you&rsquo;ve authored, notifications, and sign-in links. Just your own
            data, nothing else.
          </p>
          <Button variant="outline" onClick={onExportMyData} disabled={exporting}>
            {exporting ? 'Preparing…' : 'Export your data'}
          </Button>
        </div>
      </Card>

      <Card className="space-y-3 p-6">
        <span className={labelClass}>Account</span>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-md text-xs leading-relaxed text-muted">
            Sign out of every active session on all your devices — useful if you&rsquo;ve signed in
            somewhere you no longer trust.
          </p>
          <Button variant="danger" onClick={onSignOutAll} disabled={signingOut}>
            {signingOut ? 'Signing out…' : 'Sign out of all devices'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
